import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';
import { avatarGradient } from './avatar-color';

export interface ProfileMenuUser {
  name?: string | null;
  email?: string | null;
  /** Role label shown as a tag in the menu header (e.g. "Superadmin"). */
  roleLabel?: string | null;
  roleTone?: 'brand' | 'safe' | 'warn' | 'danger' | 'info';
}

export interface ProfileMenuLanguage {
  code: string;
  label: string;
}

export interface ProfileMenuProps {
  user: ProfileMenuUser;
  /** Optional link to the account settings page. Hidden when omitted (e.g. when already inside /account). */
  accountHref?: string;
  /** Languages to offer in the menu. Hidden when empty. */
  languages?: ReadonlyArray<ProfileMenuLanguage>;
  currentLanguage?: string;
  onLanguageChange?: (code: string) => void;
  onLogout: () => void;
  /** i18n labels. Defaults are English to keep the component standalone. */
  labels?: {
    accountSettings?: string;
    language?: string;
    logout?: string;
    openMenu?: string;
  };
  /** data-testid prefix; resolves to `${prefix}-trigger`, `${prefix}-menu`, etc. */
  testIdPrefix?: string;
}

const styles = stylex.create({
  root: { position: 'relative', display: 'inline-flex' },
  trigger: {
    appearance: 'none',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: `1px solid ${sentinel.borderEmphasis}`,
    cursor: 'pointer',
    color: sentinel.textOnAccent,
    fontFamily: sentinel.fontDisplay,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    backgroundImage: `linear-gradient(135deg, var(--avatar-c1, ${sentinel.brandPrimary}), var(--avatar-c2, ${sentinel.accentAction}))`,
    backgroundColor: 'transparent',
    display: 'grid',
    placeItems: 'center',
    transitionProperty: 'box-shadow, transform',
    transitionDuration: sentinel.motionFast,
    transitionTimingFunction: sentinel.easeOut,
    boxShadow: { default: 'none', ':hover': sentinel.shadowGlow },
  },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    minWidth: '260px',
    backgroundColor: sentinel.surfaceElevated,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    borderRadius: '0.75rem',
    boxShadow: sentinel.shadowMedium,
    padding: '0.375rem',
    display: 'grid',
    gap: '0.125rem',
    zIndex: 20,
  },
  header: {
    padding: '0.625rem 0.75rem 0.75rem',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: sentinel.borderDefault,
    marginBottom: '0.25rem',
    display: 'grid',
    gap: '0.25rem',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  headerName: {
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: sentinel.textBody,
  },
  headerEmail: {
    fontSize: '0.8125rem',
    color: sentinel.textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  roleBadge: {
    fontSize: '0.625rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontWeight: 700,
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    color: sentinel.textOnAccent,
  },
  sectionLabel: {
    fontSize: '0.625rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: sentinel.textSubtle,
    padding: '0.5rem 0.75rem 0.25rem',
  },
  item: {
    appearance: 'none',
    border: 0,
    background: 'transparent',
    backgroundColor: { default: 'transparent', ':hover': sentinel.stateHover },
    color: sentinel.textBody,
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    textAlign: 'left',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    textDecoration: 'none',
  },
  itemDanger: { color: sentinel.textError },
  itemCheck: {
    width: '14px',
    height: '14px',
    display: 'grid',
    placeItems: 'center',
    color: sentinel.accentAction,
  },
  divider: {
    height: '1px',
    backgroundColor: sentinel.borderDefault,
    marginTop: '0.25rem',
    marginBottom: '0.25rem',
  },
});

const roleTones = stylex.create({
  brand: { backgroundColor: sentinel.brandPrimary },
  safe: { backgroundColor: sentinel.riskSafe },
  warn: { backgroundColor: sentinel.riskMedium },
  danger: { backgroundColor: sentinel.riskHigh },
  info: { backgroundColor: sentinel.riskInfo },
});

function initialsOf(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email && email.split('@')[0]) || '';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
  return source.slice(0, 2).toUpperCase() || '··';
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="2 7 5.5 10.5 12 4" />
    </svg>
  );
}

export function ProfileMenu({
  user,
  accountHref,
  languages = [],
  currentLanguage,
  onLanguageChange,
  onLogout,
  labels = {},
  testIdPrefix = 'profile-menu',
}: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = initialsOf(user.name, user.email);
  const showHeader = Boolean(user.name || user.email || user.roleLabel);
  const showLanguages = languages.length > 0 && onLanguageChange;

  // Deterministic per-user gradient seeded by email (falls back to name/initials).
  const { c1, c2 } = avatarGradient(user.email || user.name || initials);
  const triggerProps = stylex.props(styles.trigger);
  const triggerStyle = {
    ...triggerProps.style,
    '--avatar-c1': c1,
    '--avatar-c2': c2,
  } as CSSProperties;

  return (
    <div ref={rootRef} {...stylex.props(styles.root)}>
      <button
        ref={triggerRef}
        type="button"
        {...triggerProps}
        style={triggerStyle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={labels.openMenu ?? 'Open account menu'}
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdPrefix}-trigger`}
      >
        {initials}
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          {...stylex.props(styles.menu)}
          data-testid={`${testIdPrefix}-menu`}
        >
          {showHeader ? (
            <div {...stylex.props(styles.header)}>
              <div {...stylex.props(styles.headerRow)}>
                {user.name ? <span {...stylex.props(styles.headerName)}>{user.name}</span> : null}
                {user.roleLabel ? (
                  <span
                    {...stylex.props(styles.roleBadge, roleTones[user.roleTone ?? 'brand'])}
                  >
                    {user.roleLabel}
                  </span>
                ) : null}
              </div>
              {user.email ? (
                <span {...stylex.props(styles.headerEmail)} title={user.email}>
                  {user.email}
                </span>
              ) : null}
            </div>
          ) : null}

          {accountHref ? (
            <a
              href={accountHref}
              role="menuitem"
              {...stylex.props(styles.item)}
              onClick={() => setOpen(false)}
              data-testid={`${testIdPrefix}-account`}
            >
              {labels.accountSettings ?? 'Account settings'}
            </a>
          ) : null}

          {showLanguages ? (
            <>
              {accountHref ? <div {...stylex.props(styles.divider)} aria-hidden="true" /> : null}
              <div {...stylex.props(styles.sectionLabel)}>
                {labels.language ?? 'Language'}
              </div>
              {languages.map((lang) => {
                const isCurrent = lang.code === currentLanguage;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isCurrent}
                    {...stylex.props(styles.item)}
                    onClick={() => {
                      onLanguageChange!(lang.code);
                      setOpen(false);
                    }}
                    data-testid={`${testIdPrefix}-lang-${lang.code}`}
                  >
                    <span>{lang.label}</span>
                    {isCurrent ? (
                      <span {...stylex.props(styles.itemCheck)} aria-hidden="true">
                        <CheckIcon />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </>
          ) : null}

          <div {...stylex.props(styles.divider)} aria-hidden="true" />
          <button
            type="button"
            role="menuitem"
            {...stylex.props(styles.item, styles.itemDanger)}
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            data-testid={`${testIdPrefix}-logout`}
          >
            {labels.logout ?? 'Log out'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export type ProfileMenuLabels = NonNullable<ProfileMenuProps['labels']>;
